import React from 'react';
import { Tag, Store, Calendar, CheckCircle, XCircle, Clock } from 'lucide-react';
import { useCurrency } from '../context/CurrencyContext';

const statusConfig = {
  active: { label: 'Active', color: 'text-emerald-600 bg-emerald-50 border-emerald-200', icon: CheckCircle },
  redeemed: { label: 'Redeemed', color: 'text-gray-500 bg-gray-100 border-gray-200', icon: CheckCircle },
  suspended: { label: 'Suspended', color: 'text-red-600 bg-red-50 border-red-200', icon: XCircle },
  expired: { label: 'Expired', color: 'text-orange-600 bg-orange-50 border-orange-200', icon: Clock },
};

export default function VoucherCard({ voucher, showQR = false, onClick }) {
  const cfg = statusConfig[voucher.status] || statusConfig.active;
  const StatusIcon = cfg.icon;
  const { formatMoney, formatDate } = useCurrency();
  const amount = formatMoney(voucher.amount);

  return (
    <div
      className={`relative bg-white rounded-2xl overflow-hidden shadow-[0_8px_30px_-8px_rgba(86,29,26,0.15)] border border-[#D4AF37]/20 transition-all duration-300 hover:shadow-[0_20px_40px_-10px_rgba(86,29,26,0.2)] hover:-translate-y-1 ${onClick ? 'cursor-pointer' : ''}`}
      onClick={onClick}
      data-testid="voucher-card"
    >
      {/* Top accent bar */}
      <div className="h-1.5 bg-gradient-to-r from-[#561D1A] via-[#EC1A23] to-[#D4AF37]" />

      <div className="p-5">
        {/* Header */}
        <div className="flex items-start justify-between mb-4">
          <div>
            <p className="text-xs text-gray-400 font-body uppercase tracking-wider mb-0.5">Gift Voucher</p>
            <p className="text-sm font-medium text-gray-700 font-body">{voucher.type_name || 'Universal Voucher'}</p>
          </div>
          <span className={`inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full border font-medium ${cfg.color}`} data-testid="voucher-status">
            <StatusIcon size={11} />
            {cfg.label}
          </span>
        </div>

        {/* Amount */}
        <div className="text-center py-3 mb-4 bg-gradient-to-br from-[#561D1A]/5 to-[#EC1A23]/5 rounded-xl">
          <p className="font-heading text-3xl font-bold text-[#561D1A]" data-testid="voucher-amount">{amount}</p>
        </div>

        {/* Details */}
        <div className="space-y-2">
          <div className="flex items-center gap-2 text-xs text-gray-500">
            <Tag size={12} className="text-[#D4AF37]" />
            <span className="font-mono tracking-wider font-medium text-gray-700" data-testid="voucher-code">{voucher.code}</span>
          </div>
          {voucher.redeemed_by_store_name && (
            <div className="flex items-center gap-2 text-xs text-gray-500">
              <Store size={12} className="text-[#D4AF37]" />
              <span>Redeemed at {voucher.redeemed_by_store_name}</span>
            </div>
          )}
          {voucher.expiry_date && (
            <div className="flex items-center gap-2 text-xs text-gray-500">
              <Calendar size={12} className="text-[#D4AF37]" />
              <span>Expires {formatDate(voucher.expiry_date)}</span>
            </div>
          )}
        </div>

        {showQR && voucher.qr_data && (
          <div className="mt-4 pt-4 border-t border-gray-100 flex justify-center">
            <p className="text-xs text-gray-400 font-mono">{voucher.qr_data}</p>
          </div>
        )}
      </div>
    </div>
  );
}
